import { createLazyResource, createMutation, toast, useLocalization } from '@magicdoorinc/ui';
import { createMemo } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { letterTemplateRepository } from '~/repositories/letterTemplateRepository';
import type {
  CreateLetterTemplateGroupDto,
  LetterTemplateGroupDto,
  PaginationDto,
  UpdateLetterTemplateGroupDto,
} from '~/repositories/letterTemplateRepository';

export const [LetterTemplateGroupProvider, useLetterTemplateGroup] = createMagicDoorContext('LetterTemplateGroup', () => {
  const { t } = useLocalization();

  const [groups, { refetch, mutate }] = createLazyResource<PaginationDto<LetterTemplateGroupDto>>(() =>
    letterTemplateRepository.getGroups({ pageSize: 100 })
  );

  const sortedGroups = createMemo(() => {
    const items = groups()?.items;
    if (!items) return [];
    return [...items].sort((a, b) => a.name.localeCompare(b.name));
  });

  const groupOptions = createMemo(() => [
    { label: t('Ungrouped'), value: '' },
    ...sortedGroups().map((group) => ({ label: group.name, value: group.id })),
  ]);

  const addGroup = createMutation(async (payload: CreateLetterTemplateGroupDto) => {
    const result = await letterTemplateRepository.createGroup(payload);
    toast.success(t('{name} has been created successfully', { name: t('Group') }));
    refetch();
    return result;
  });

  const updateGroup = createMutation(async ({ id, payload }: { id: string; payload: UpdateLetterTemplateGroupDto }) => {
    const result = await letterTemplateRepository.updateGroup(id, payload);
    toast.success(t('{name} has been updated successfully', { name: t('Group') }));
    mutate(
      (previous) =>
        previous && {
          ...previous,
          items: previous.items.map((group) => (group.id === id ? result : group)),
        }
    );
    return result;
  });

  const deleteGroup = createMutation(async (id: string) => {
    await letterTemplateRepository.deleteGroup(id);
    toast.success(t('{name} has been deleted successfully', { name: t('Group') }));
    mutate(
      (previous) =>
        previous && {
          ...previous,
          items: previous.items.filter((group) => group.id !== id),
        }
    );
  });

  return {
    groups,
    sortedGroups,
    groupOptions,
    addGroup,
    updateGroup,
    deleteGroup,
    refetch,
  };
});
